import React, { useEffect, useState } from "react";
import { MapPin, Pencil, Star, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COUNTRIES, isUS, regionLabel, postalLabel, countryName } from "@/lib/countries";

const BLANK = { name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" };
const pickFields = (a) => ({ name: a.name || "", phone: a.phone || "", street1: a.street1 || "", street2: a.street2 || "", city: a.city || "", state: a.state || "", zip: a.zip || "", country: a.country || "US" });

// Reusable address book used inside checkout / cart / donation.
// Buyers can pick a saved address, edit it, set a different default, or add a new one — all inline.
// Reports the effective shipping address to the parent via onChange(addr).
export default function AddressPicker({ onChange, warnings = [], testPrefix = "ap", initial = null }) {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [selId, setSelId] = useState("new");     // saved id | "new"
  const [editingId, setEditingId] = useState(null);
  const [saveNew, setSaveNew] = useState(true);
  const [busy, setBusy] = useState(false);

  const typedRef = React.useRef(false);
  const set = (k) => (e) => { const v = e.target.value; typedRef.current = true; const next = { ...form, [k]: v }; setForm(next); onChange(next); };
  const emit = (a) => { onChange(a); };

  const load = async (preferId) => {
    try {
      const d = await api.get("/addresses");
      const list = d.addresses || [];
      setSaved(list);
      // Preserve an address the buyer already typed (e.g. after clicking "Edit address" or while this fetch was in flight).
      if (!preferId && (typedRef.current || (initial && String(initial.street1 || "").trim()))) {
        if (initial && String(initial.street1 || "").trim() && !typedRef.current) {
          const f = pickFields(initial); setSelId("new"); setForm(f); setSaveNew(false); emit(f);
        }
        return list;
      }
      const chosen = (preferId && list.find((a) => a.id === preferId)) || list.find((a) => a.is_default) || list[0];
      if (chosen) { setSelId(chosen.id); setForm(pickFields(chosen)); emit(pickFields(chosen)); }
      else { setSelId("new"); setForm(BLANK); setSaveNew(true); emit(BLANK); }
      return list;
    } catch { setSaved([]); return []; }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectSaved = (a) => { setEditingId(null); setSelId(a.id); const f = pickFields(a); setForm(f); emit(f); };
  const startNew = () => { setEditingId(null); setSelId("new"); setForm(BLANK); setSaveNew(true); emit(BLANK); };
  const startEdit = (a) => { setEditingId(a.id); setSelId(a.id); const f = pickFields(a); setForm(f); emit(f); };

  const setDefault = async (a) => {
    setBusy(true);
    try { await api.patch(`/addresses/${a.id}`, { ...pickFields(a), is_default: true }); await load(a.id); toast.success("Default address updated"); }
    catch (e) { toast.error(e.message || "Could not update"); } finally { setBusy(false); }
  };

  const required = () => {
    const us = isUS(form.country);
    const fields = us
      ? [["name", "Full name"], ["street1", "Street address"], ["city", "City"], ["state", "State"], ["zip", "ZIP code"]]
      : [["name", "Full name"], ["street1", "Street address"], ["city", "City"], ["zip", "Postal code"]];
    for (const [k, label] of fields) {
      if (!String(form[k]).trim()) { toast.error(`${label} is required`); return false; }
    }
    return true;
  };

  const saveEdit = async () => {
    if (!required()) return;
    setBusy(true);
    try { await api.patch(`/addresses/${editingId}`, { ...form, is_default: (saved.find((a) => a.id === editingId) || {}).is_default || false }); const id = editingId; setEditingId(null); await load(id); toast.success("Address updated"); }
    catch (e) { toast.error(e.message || "Could not save"); } finally { setBusy(false); }
  };

  const saveNewToBook = async () => {
    if (!required()) return;
    setBusy(true);
    try { const d = await api.post("/addresses", { ...form, is_default: (saved || []).length === 0 }); const list = d.addresses || []; const created = list[list.length - 1]; setSaved(list); if (created) selectSaved(created); toast.success("Address saved to your book"); }
    catch (e) { toast.error(e.message || "Could not save"); } finally { setBusy(false); }
  };

  if (saved === null) return <div className="ss-muted" style={{ fontSize: 13 }}>Loading addresses…</div>;

  const adding = selId === "new" && !editingId;
  const editing = !!editingId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid={`${testPrefix}-root`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14 }}><MapPin size={16} /> Shipping address</div>

      {saved.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid={`${testPrefix}-saved-list`}>
          {saved.map((a) => {
            const active = selId === a.id;
            return (
              <div key={a.id} onClick={() => selectSaved(a)} data-testid={`${testPrefix}-saved-${a.id}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, cursor: "pointer", border: `1.5px solid ${active ? "var(--brand)" : "var(--line)"}`, background: active ? "var(--gold-soft)" : "transparent" }}>
                <span style={{ width: 18, height: 18, borderRadius: 999, border: `2px solid ${active ? "var(--brand)" : "var(--line)"}`, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  {active && <span style={{ width: 9, height: 9, borderRadius: 999, background: "var(--brand)" }} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                    {a.name}
                    {a.is_default && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: "var(--brand)", color: "#fff", fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 999 }} data-testid={`${testPrefix}-default-${a.id}`}><Star size={9} /> Default</span>}
                  </div>
                  <div className="ss-muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.street1}{a.street2 ? `, ${a.street2}` : ""} · {a.city}{a.state ? `, ${a.state}` : ""} {a.zip}{!isUS(a.country) ? ` · ${countryName(a.country)}` : ""}</div>
                </div>
                {!a.is_default && <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={(e) => { e.stopPropagation(); setDefault(a); }} disabled={busy} data-testid={`${testPrefix}-setdefault-${a.id}`} title="Set as default"><Star size={13} /></button>}
                <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={(e) => { e.stopPropagation(); startEdit(a); }} data-testid={`${testPrefix}-edit-${a.id}`} title="Edit address"><Pencil size={13} /></button>
              </div>
            );
          })}
          {!adding && <button type="button" className="gl-chip" style={{ alignSelf: "flex-start" }} onClick={startNew} data-testid={`${testPrefix}-add-new`}><Plus size={13} /> Add a new address</button>}
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{ background: "var(--gold-soft)", borderRadius: 10, padding: 10, fontSize: 13 }} data-testid={`${testPrefix}-warnings`}>
          <div style={{ fontWeight: 700, color: "var(--gold-2)" }}>Please fix your address</div>
          <ul style={{ margin: "6px 0 0 18px" }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {(adding || editing) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, border: saved.length ? "1px solid var(--line)" : "none", borderRadius: 12, padding: saved.length ? 12 : 0 }} data-testid={`${testPrefix}-form`}>
          {saved.length > 0 && <div style={{ fontWeight: 700, fontSize: 13 }}>{editing ? "Edit address" : "New address"}</div>}
          <div className="ss-field">
            <select value={form.country || "US"} onChange={set("country")} data-testid={`${testPrefix}-country`}
              style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 14, cursor: "pointer" }}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div className="ss-field"><input placeholder="Full name" value={form.name} onChange={set("name")} data-testid={`${testPrefix}-name`} /></div>
          <div className="ss-field"><input placeholder="Street address" value={form.street1} onChange={set("street1")} data-testid={`${testPrefix}-street1`} /></div>
          <div className="ss-field"><input placeholder="Apt / suite (optional)" value={form.street2} onChange={set("street2")} data-testid={`${testPrefix}-street2`} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8 }}>
            <div className="ss-field"><input placeholder="City" value={form.city} onChange={set("city")} data-testid={`${testPrefix}-city`} /></div>
            <div className="ss-field"><input placeholder={regionLabel(form.country)} value={form.state} onChange={set("state")} maxLength={isUS(form.country) ? 2 : undefined} data-testid={`${testPrefix}-state`} /></div>
            <div className="ss-field"><input placeholder={postalLabel(form.country)} value={form.zip} onChange={set("zip")} data-testid={`${testPrefix}-zip`} /></div>
          </div>
          {editing ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="ss-btn ss-btn-primary ss-btn-sm" onClick={saveEdit} disabled={busy} data-testid={`${testPrefix}-save-edit`}><Check size={14} /> {busy ? "Saving…" : "Save changes"}</button>
              <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => { setEditingId(null); const a = saved.find((x) => x.id === selId); if (a) selectSaved(a); }} data-testid={`${testPrefix}-cancel-edit`}>Cancel</button>
            </div>
          ) : (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }} data-testid={`${testPrefix}-save-label`}>
                <input type="checkbox" checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)} data-testid={`${testPrefix}-save-toggle`} />
                Save this address to my address book
              </label>
              {saveNew && <button type="button" className="ss-btn ss-btn-ghost ss-btn-sm" style={{ alignSelf: "flex-start" }} onClick={saveNewToBook} disabled={busy} data-testid={`${testPrefix}-save-new`}><Plus size={14} /> {busy ? "Saving…" : "Save to my addresses"}</button>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
